import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Loader2, Minus, Plus, FileText, CheckCircle2, Truck, Store, PencilLine } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const UNIT_PRICE = 40;

const initial = {
  store_name: "", poc_name: "", email: "", phone: "",
  address: "", city: "", state: "", zip: "", quantity: 1, notes: "",
};

export default function Preorder() {
  const [form, setForm] = useState(initial);
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(null); // order object
  const [retailers, setRetailers] = useState([]);
  const [retailerId, setRetailerId] = useState(""); // selected existing store id ("" = new store)
  const [orderMode, setOrderMode] = useState(null); // null (choose) | "existing" | "new"
  const [isRetailer, setIsRetailer] = useState(() => {
    try { return sessionStorage.getItem("hpg_retailer_ok") === "true"; } catch { return false; }
  });

  const confirmRetailer = () => {
    try { sessionStorage.setItem("hpg_retailer_ok", "true"); } catch {}
    setIsRetailer(true);
  };

  const chooseNew = () => {
    setOrderMode("new");
    setRetailerId("");
    setForm((f) => ({ ...f, store_name: "", address: "", city: "", state: "", zip: "" }));
  };
  const backToChoice = () => {
    setOrderMode(null);
    setRetailerId("");
    setForm((f) => ({ ...f, store_name: "", address: "", city: "", state: "", zip: "" }));
  };

  useEffect(() => {
    if (!isRetailer) return;
    (async () => {
      try {
        const res = await fetch(`${API}/retailers`);
        const data = await res.json();
        setRetailers(Array.isArray(data) ? data : []);
      } catch { setRetailers([]); }
    })();
  }, [isRetailer]);

  const selectStore = async (e) => {
    const id = e.target.value;
    setRetailerId(id);
    if (!id) {
      // switched to "new store" -> clear store identity fields, keep contact/qty
      setForm((f) => ({ ...f, store_name: "", address: "", city: "", state: "", zip: "" }));
      return;
    }
    const r = retailers.find((x) => x.id === id);
    if (r) {
      setForm((f) => ({
        ...f,
        store_name: r.name || "", address: r.address || "", city: r.city || "",
        state: r.state || "", zip: r.zip || "", phone: r.phone || f.phone,
      }));
    }
    // prefill point-of-contact + email from registration (editable)
    try {
      const res = await fetch(`${API}/retailers/${id}/contact`);
      if (res.ok) {
        const c = await res.json();
        setForm((f) => ({ ...f, poc_name: c.poc_name || f.poc_name, email: c.email || f.email }));
      }
    } catch {}
  };

  const isExisting = !!retailerId;
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const bump = (delta) =>
    setForm((f) => ({ ...f, quantity: Math.max(1, Math.min(10000, Number(f.quantity || 1) + delta)) }));

  const total = (Number(form.quantity) || 0) * UNIT_PRICE;

  const submit = async (e) => {
    e.preventDefault();
    if (!form.store_name || !form.poc_name || !form.email || !form.phone || !form.address || !form.city || !form.state) {
      toast.error("Please complete all required fields.");
      return;
    }
    setSending(true);
    try {
      const res = await fetch(`${API}/preorders`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...form, quantity: Number(form.quantity), retailer_id: retailerId || null }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(typeof data.detail === "string" ? data.detail : "Submission failed");
      setDone(data.order);
      toast.success(`Preorder ${data.order.invoice_no} received.`);
    } catch (err) {
      toast.error(err.message || "Something went wrong. Please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="preorder" data-testid="preorder" className="py-24 sm:py-32 bg-surface border-y border-surface-border">
      <div className="mx-auto max-w-4xl px-6">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="mb-12"
        >
          <p className="font-mono text-xs uppercase tracking-[0.3em] text-blaze mb-4">Wholesale Preorder</p>
          <h2 className="font-display uppercase text-5xl sm:text-6xl lg:text-7xl leading-[0.9]">
            Stock <span className="text-fire">us</span>
          </h2>
          <p className="text-muted-foreground mt-5 text-base sm:text-lg">
            Retailers, preorder 25-jar grabba case refills. We deliver and collect payment
            on delivery (COD). You'll get an instant invoice for your records.
          </p>
          {isRetailer && !done && (
            <p className="font-mono text-sm text-gold mt-3">{money(UNIT_PRICE)} flat per 25-jar grabba case · Cash on delivery</p>
          )}
        </motion.div>

        {done ? (
          <Confirmation order={done} onReset={() => { setForm(initial); setDone(null); setOrderMode(null); setRetailerId(""); }} />
        ) : !isRetailer ? (
          <RetailerGate onConfirm={confirmRetailer} />
        ) : orderMode === null && retailers.length > 0 ? (
          <ModeChooser onRestock={() => setOrderMode("existing")} onNew={chooseNew} />
        ) : (
          <motion.form
            onSubmit={submit}
            data-testid="preorder-form"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="space-y-6"
          >
            {orderMode === "existing" && (
              <div className="bg-black border border-gold/40 p-5">
                <Field label="Select your store to restock">
                  <select data-testid="pre-existing-store" value={retailerId} onChange={selectStore} className={inputCls} autoFocus>
                    <option value="">— Choose your store —</option>
                    {retailers.map((r) => (
                      <option key={r.id} value={r.id}>{r.name} — {r.address}, {r.city} {r.state}</option>
                    ))}
                  </select>
                </Field>
                {isExisting ? (
                  <p className="text-gold/90 text-xs mt-3 flex items-center gap-1.5">
                    <PencilLine size={13} /> Store details filled from your listing. Just add your contact info and quantity below.
                  </p>
                ) : (
                  <p className="text-muted-foreground text-xs mt-3">
                    Not listed?{" "}
                    <button type="button" data-testid="pre-switch-new" onClick={chooseNew} className="text-blaze underline underline-offset-2">Register a new store instead</button>.
                  </p>
                )}
              </div>
            )}

            {(orderMode === "new" || (orderMode === "existing" && isExisting)) && (
            <>
            <Field label="Store / Business Name *">
              <input data-testid="pre-store" value={form.store_name} onChange={set("store_name")} readOnly={isExisting} className={`${inputCls} ${isExisting ? "opacity-70 cursor-not-allowed" : ""}`} placeholder="Store name" />
            </Field>
            <div className="grid sm:grid-cols-2 gap-6">
              <Field label="Point of Contact *">
                <input data-testid="pre-poc" value={form.poc_name} onChange={set("poc_name")} className={inputCls} placeholder="Full name" />
              </Field>
              <Field label="Phone *">
                <input data-testid="pre-phone" value={form.phone} onChange={set("phone")} className={inputCls} placeholder="(555) 000-0000" />
              </Field>
            </div>
            <Field label="Email *">
              <input data-testid="pre-email" type="email" value={form.email} onChange={set("email")} className={inputCls} placeholder="store@email.com" />
            </Field>
            <Field label="Delivery Address *">
              <input data-testid="pre-address" value={form.address} onChange={set("address")} readOnly={isExisting} className={`${inputCls} ${isExisting ? "opacity-70 cursor-not-allowed" : ""}`} placeholder="Street address" />
            </Field>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-6">
              <Field label="City *"><input data-testid="pre-city" value={form.city} onChange={set("city")} readOnly={isExisting} className={`${inputCls} ${isExisting ? "opacity-70 cursor-not-allowed" : ""}`} /></Field>
              <Field label="State *"><input data-testid="pre-state" value={form.state} onChange={set("state")} readOnly={isExisting} className={`${inputCls} ${isExisting ? "opacity-70 cursor-not-allowed" : ""}`} placeholder="NC" /></Field>
              <Field label="ZIP"><input data-testid="pre-zip" value={form.zip} onChange={set("zip")} readOnly={isExisting} className={`${inputCls} ${isExisting ? "opacity-70 cursor-not-allowed" : ""}`} /></Field>
            </div>

            <div className="grid sm:grid-cols-2 gap-6 items-end">
              <Field label="25-Jar Grabba Cases — Quantity *">
                <div className="flex items-stretch border border-surface-border">
                  <button type="button" data-testid="pre-qty-minus" onClick={() => bump(-1)} className="px-4 bg-black text-white hover:bg-blaze transition-colors" aria-label="Decrease"><Minus size={16} /></button>
                  <input
                    data-testid="pre-qty"
                    type="number" min={1} value={form.quantity}
                    onChange={(e) => setForm((f) => ({ ...f, quantity: e.target.value.replace(/[^0-9]/g, "") }))}
                    onBlur={() => setForm((f) => ({ ...f, quantity: Math.max(1, Number(f.quantity) || 1) }))}
                    className="flex-1 bg-black text-white text-center text-lg font-heading focus:outline-none"
                  />
                  <button type="button" data-testid="pre-qty-plus" onClick={() => bump(1)} className="px-4 bg-black text-white hover:bg-blaze transition-colors" aria-label="Increase"><Plus size={16} /></button>
                </div>
              </Field>
              <div className="bg-black border border-surface-border p-5 flex items-center justify-between">
                <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-muted-foreground">Estimated Total (COD)</span>
                <span data-testid="pre-total" className="font-display text-3xl text-fire">{money(total)}</span>
              </div>
            </div>

            <Field label="Delivery notes (optional)">
              <textarea data-testid="pre-notes" value={form.notes} onChange={set("notes")} rows={3} className={`${inputCls} resize-none`} placeholder="Best delivery times, dock/entrance info, etc." />
            </Field>

            <button
              data-testid="pre-submit"
              type="submit"
              disabled={sending}
              className="group inline-flex items-center justify-center gap-2 w-full sm:w-auto bg-blaze text-white font-heading uppercase text-sm tracking-wider py-4 px-10 hover:bg-gold hover:text-ink transition-colors duration-200 disabled:opacity-60"
            >
              {sending ? <Loader2 className="animate-spin" size={18} /> : <Truck size={18} className="transition-transform duration-200 group-hover:translate-x-1" />}
              {sending ? "Submitting…" : "Submit Preorder"}
            </button>
            <p className="text-muted-foreground/70 text-xs">
              This is a preorder request, not a charge. Payment is collected in cash on delivery. Custom quotes for special
              orders? <a href="#contact" className="text-blaze underline underline-offset-4">Contact us</a>.
            </p>
            </>
            )}
            {retailers.length > 0 && (
              <button type="button" data-testid="pre-back-choice" onClick={backToChoice} className="font-mono text-[11px] uppercase tracking-[0.2em] text-muted-foreground hover:text-blaze transition-colors">
                ← Back to options
              </button>
            )}
          </motion.form>
        )}
      </div>
    </section>
  );
}

function ModeChooser({ onRestock, onNew }) {
  return (
    <motion.div
      data-testid="preorder-mode-chooser"
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.6 }}
      className="grid sm:grid-cols-2 gap-4 max-w-3xl"
    >
      <button
        type="button"
        data-testid="mode-restock"
        onClick={onRestock}
        className="group text-left border border-gold/40 bg-black p-7 hover:border-gold hover:bg-surface transition-colors duration-200"
      >
        <Store className="text-gold mb-4" size={26} />
        <h3 className="font-heading uppercase text-lg tracking-tight mb-2">Restock my store</h3>
        <p className="text-muted-foreground text-sm">Already a listed retailer? Pick your store and we'll fill in your details automatically.</p>
      </button>
      <button
        type="button"
        data-testid="mode-new"
        onClick={onNew}
        className="group text-left border border-surface-border bg-black p-7 hover:border-blaze hover:bg-surface transition-colors duration-200"
      >
        <Plus className="text-blaze mb-4" size={26} />
        <h3 className="font-heading uppercase text-lg tracking-tight mb-2">Register a new store</h3>
        <p className="text-muted-foreground text-sm">First time ordering? Enter your store details to set up your account.</p>
      </button>
    </motion.div>
  );
}

function RetailerGate({ onConfirm }) {
  return (
    <motion.div
      data-testid="preorder-retailer-gate"
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.6 }}
      className="border border-surface-border bg-black p-8 sm:p-10 max-w-2xl"
    >
      <div className="flex items-center gap-3 mb-5">
        <Store className="text-blaze" size={24} />
        <h3 className="font-display uppercase text-2xl leading-none">Retailers only</h3>
      </div>
      <p className="text-muted-foreground text-sm sm:text-base mb-8">
        Wholesale ordering and pricing are for licensed tobacco and smoke-shop retailers.
        Confirm you're a retailer to continue to the order form and pricing.
      </p>
      <div className="flex flex-col sm:flex-row gap-4">
        <button
          data-testid="pre-retailer-confirm"
          onClick={onConfirm}
          className="group inline-flex items-center justify-center gap-2 bg-blaze text-white font-heading uppercase text-sm tracking-wider py-4 px-8 hover:bg-gold hover:text-ink transition-colors duration-200"
        >
          <Store size={18} className="transition-transform duration-200 group-hover:scale-110" />
          Yes, I'm a retailer
        </button>
        <a
          data-testid="pre-retailer-shopper"
          href="#where-to-buy"
          className="inline-flex items-center justify-center gap-2 border border-surface-border text-muted-foreground font-heading uppercase text-sm tracking-wider py-4 px-8 hover:border-white hover:text-white transition-colors duration-200"
        >
          I'm a shopper, find a store
        </a>
      </div>
    </motion.div>
  );
}

function Confirmation({ order, onReset }) {
  const pdfUrl = `${API}/orders/${order.id}/invoice.pdf`;
  return (
    <motion.div
      data-testid="preorder-confirmation"
      initial={{ opacity: 0, scale: 0.98 }}
      animate={{ opacity: 1, scale: 1 }}
      className="border border-gold/40 bg-black p-8 sm:p-10"
    >
      <div className="flex items-center gap-3 mb-6">
        <CheckCircle2 className="text-gold" size={28} />
        <div>
          <h3 className="font-display uppercase text-2xl leading-none">Preorder received</h3>
          <p className="text-muted-foreground text-sm mt-1">Invoice <span className="text-gold font-mono">{order.invoice_no}</span></p>
        </div>
      </div>
      <div className="grid sm:grid-cols-2 gap-6 mb-8">
        <Detail label="Store" value={order.store_name} />
        <Detail label="Contact" value={order.poc_name} />
        <Detail label="Quantity" value={`${order.quantity} × 25-Jar Grabba Case`} />
        <Detail label="Total Due (COD)" value={money(order.total)} highlight />
      </div>
      <p className="text-muted-foreground text-sm mb-6">
        A copy of this invoice has been emailed to <span className="text-white">{order.email}</span>. Our team will
        confirm your delivery route shortly. Keep your invoice number for your records.
      </p>
      <div className="flex flex-wrap gap-3">
        <a
          data-testid="pre-download-invoice"
          href={pdfUrl} target="_blank" rel="noreferrer"
          className="inline-flex items-center gap-2 bg-blaze text-white font-heading uppercase text-sm tracking-wider py-3.5 px-6 hover:bg-gold hover:text-ink transition-colors"
        >
          <FileText size={17} /> Download Invoice (PDF)
        </a>
        <button
          data-testid="pre-new-order"
          onClick={onReset}
          className="inline-flex items-center gap-2 border border-surface-border font-heading uppercase text-sm tracking-wider py-3.5 px-6 hover:border-white transition-colors"
        >
          Place another order
        </button>
      </div>
    </motion.div>
  );
}

function money(n) {
  return `$${Number(n || 0).toFixed(2)}`;
}

const inputCls =
  "w-full bg-black border border-surface-border text-white px-4 py-3.5 text-sm focus:outline-none focus:border-blaze focus:ring-1 focus:ring-blaze transition-colors duration-200 placeholder:text-muted-foreground/50";

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-muted-foreground mb-2 block">{label}</span>
      {children}
    </label>
  );
}

function Detail({ label, value, highlight }) {
  return (
    <div>
      <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-muted-foreground mb-1">{label}</p>
      <p className={`font-heading ${highlight ? "text-fire text-2xl" : "text-white text-base"}`}>{value}</p>
    </div>
  );
}
